import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth/context'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.miac.app'

let cachedResult: boolean | null = null
let cachedUserId: string | null = null

async function fetchAdminStatus(accessToken: string | undefined): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/check`, {
      headers: {
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      },
    })
    if (response.ok) {
      const data = await response.json()
      return data.isAdmin === true
    }
  } catch {
    // Server unreachable = not admin
  }
  return false
}

/**
 * Server-side only admin check. No admin identity info lives in the frontend.
 * Calls the backend /api/admin/check endpoint which verifies the user's
 * identity and returns whether they have admin access.
 */
export function useAdmin(): { isAdmin: boolean; loading: boolean } {
  const { user, loading: authLoading } = useAuth()

  // Derive synchronous state: what do we already know without fetching?
  const knownResult = useMemo(() => {
    if (authLoading) return { isAdmin: false, loading: true, needsFetch: false }
    if (!user) return { isAdmin: false, loading: false, needsFetch: false }
    if (cachedResult !== null && cachedUserId === user.id) {
      return { isAdmin: cachedResult, loading: false, needsFetch: false }
    }
    return { isAdmin: false, loading: true, needsFetch: true }
  }, [user, authLoading])

  const [asyncResult, setAsyncResult] = useState<{ isAdmin: boolean; userId: string } | null>(null)

  useEffect(() => {
    if (!knownResult.needsFetch || !user) return

    let cancelled = false

    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const isAdmin = await fetchAdminStatus(session?.access_token)

      if (cancelled) return

      cachedResult = isAdmin
      cachedUserId = user.id
      setAsyncResult({ isAdmin, userId: user.id })
    }

    check()

    return () => { cancelled = true }
  }, [knownResult.needsFetch, user])

  // If async fetch completed for this user, use that
  if (asyncResult && user && asyncResult.userId === user.id) {
    return { isAdmin: asyncResult.isAdmin, loading: false }
  }

  return { isAdmin: knownResult.isAdmin, loading: knownResult.loading }
}

/**
 * Standalone server-side admin check (non-hook, for use in async functions).
 * Caches the result so repeated calls in the same session are fast.
 */
export async function checkAdminServer(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return false

  if (cachedResult !== null && cachedUserId === user.id) {
    return cachedResult
  }

  const { data: { session } } = await supabase.auth.getSession()
  const isAdmin = await fetchAdminStatus(session?.access_token)

  cachedResult = isAdmin
  cachedUserId = user.id
  return isAdmin
}
